import {useEffect, useState} from 'react';

type Theme = 'light' | 'dark' | 'system';

const THEME_COOKIE = 'nudge-theme';

/**
 * Get the current theme from cookie or default to system
 */
const getThemeFromCookie = (): Theme => {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${THEME_COOKIE}=`));

  if (cookie) {
    const value = cookie.split('=')[1];
    if (value === 'light' || value === 'dark' || value === 'system') {
      return value;
    }
  }

  return 'system';
};

/**
 * Set theme cookie
 */
const setThemeCookie = (theme: Theme) => {
  document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000`; // 1 year
};

/**
 * Check if the system prefers dark mode
 */
const getSystemPrefersDark = (): boolean => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/**
 * Hook for managing dark mode with system preference detection and user override
 */
export const useDarkMode = () => {
  const [theme, setTheme] = useState<Theme>(() => getThemeFromCookie());
  const [isDark, setIsDark] = useState(() => {
    const currentTheme = getThemeFromCookie();
    if (currentTheme === 'system') {
      return getSystemPrefersDark();
    }
    return currentTheme === 'dark';
  });

  // Update dark mode state when theme changes
  useEffect(() => {
    const updateDarkMode = () => {
      if (theme === 'system') {
        setIsDark(getSystemPrefersDark());
      } else {
        setIsDark(theme === 'dark');
      }
    };

    updateDarkMode();

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        updateDarkMode();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Apply dark class to body
  useEffect(() => {
    if (isDark) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [isDark]);

  // Update theme and save to cookie
  const updateTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    setThemeCookie(newTheme);
  };

  return {
    theme,
    isDark,
    updateTheme,
  };
};
